import { useCallback, useMemo, useState } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { changeUserType, validateApiResponse } from '../../../services';
import { USER_TYPE_META } from '../constants/copy';

/**
 * Changes a member's user type through the service and tells the admin how it went: a success
 * toast (the updated API member goes to `onChanged` so the badge can update) or an error toast
 * with nothing changed.
 *
 * @param {{ onChanged?: (updated: object) => void }} [options]
 * @returns {{ changeType: (member: { id: string, displayName: string }, targetType: string) => Promise<boolean>, isSaving: boolean }}
 */
export const useChangeUserType = ({ onChanged } = {}) => {
  const { t } = useTranslation('people-access');
  const toast = useToast();
  const [isSaving, setIsSaving] = useState(false);

  const changeType = useCallback(
    async (member, targetType) => {
      setIsSaving(true);
      try {
        const res = await changeUserType(member.id, targetType);
        const { body } = validateApiResponse(res, t('members.typeChange.errorMessage'));
        onChanged?.(body);
        toast.success({
          title: t('members.typeChange.successTitle'),
          message: t('members.typeChange.successMessage', {
            name: member.displayName,
            type: t(USER_TYPE_META[targetType].labelKey),
          }),
          canClose: true,
        });
        return true;
      } catch (err) {
        toast.error({
          title: t('members.typeChange.errorTitle'),
          message: err?.message || t('members.typeChange.errorMessage'),
          canClose: true,
        });
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [onChanged, t, toast],
  );

  return useMemo(() => ({ changeType, isSaving }), [changeType, isSaving]);
};
